
// Import necessary modules and components
import useFetch from "../../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../../components/page-header/page-header";
import useToast from "../../../../hooks/useToast";
import { TFilterModel, TKPI } from "../../../../types/config";
import { eEVALUATION_CRITERIA, eResultCode } from "../../../../utils/enum";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import { defaultFilterParams, PageSize } from "../../../../utils/constants";
import {
  Button,
  Drawer,
  Dropdown,
  Input,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
  Tag,
  Modal,
} from "antd";
import CustomDrawer from "../../../../components/drawer";
import { GetKPIs, DeleteKPI } from "../../../../utils/api.constant";
import { useNavigate } from "react-router-dom";
import utils from "../../../../utils";
import { UserContext } from "../../../../context";
import {
  EditOutlined,
  FilterOutlined,
  IdcardOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
  CloseOutlined,
  DeleteOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../../components/configurable-columns";
import KPIForm from "./form";
import PerformanceForm from "./performance";

export default function KPIList() {
  const navigate = useNavigate();
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<TKPI[]>([]);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const { onShowToast } = useToast();

  const handleDelete = (record: TKPI) => {
    Modal.confirm({
      title: "Confirm Delete",
      content: `You are about to delete the ${record.name}`,
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        try {
          setIsLoading(true);
          const payload = {
            data: {
              id: record.id,
            },
          };
          const response = await post(DeleteKPI, payload);
          if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
            onShowToast({
              type: ToastType.success,
              title: <CheckOutlined />,
              position: ToastOpen.centerTop,
              content: response.dataResponse.description,
            });
            fetchData();
          } else {
            onShowToast({
              type: ToastType.error,
              title: <CloseOutlined />,
              position: ToastOpen.centerTop,
              content: response.dataResponse.description,
            });
          }
        } catch (error) {
          console.error("Error:", error);
        } finally {
          setIsLoading(false);
        }
      },
    });
  };

  const initialColumns: any[] = [
    {
      title: "Title",
      dataIndex: "name",
      key: "name",
      width: "20%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "name"),
      sorter: (a: TKPI, b: TKPI) => a.name.localeCompare(b.name),
      onFilter: (value: string | number | boolean, record: TKPI) =>
        record.name?.includes(value as string),
    },
    {
      title: "Designation",
      dataIndex: "designations",
      key: "designations",
      width: "20%",
      filterSearch: true,
      filters: [], // Will be populated in fetchData
      sorter: (a: TKPI, b: TKPI) => {
        const aName = a.designations?.map((d) => d.name).join(", ") || "";
        const bName = b.designations?.map((d) => d.name).join(", ") || "";
        return aName.localeCompare(bName);
      },
      onFilter: (value: string | number | boolean, record: TKPI) =>
        record.designations?.some((d) => d.name === value) || false,
      render: (designations: any[]) => (
        <>
          {designations?.map((d, index) => (
            <Tag key={index}>{d.name}</Tag>
          ))}
        </>
      ),
    },
    {
      title: "Criteria",
      dataIndex: "criteria",
      key: "criteria",
      width: "20%",
      filters: Object.keys(eEVALUATION_CRITERIA)
        .filter((key) => isNaN(Number(key))) // keep only enum names
        .map((key) => ({
          text: utils.formatEnumLabel(key),
          value: eEVALUATION_CRITERIA[key as keyof typeof eEVALUATION_CRITERIA],
        })),

      onFilter: (value: string | number | boolean, record: TKPI) =>
        record.criteria === value,

      render: (value: eEVALUATION_CRITERIA) => {
        const enumKey = eEVALUATION_CRITERIA[value];
        return utils.formatEnumLabel(enumKey);
      },

      sorter: (a: TKPI, b: TKPI) => {
        const aKey = eEVALUATION_CRITERIA[a.criteria!] || "";
        const bKey = eEVALUATION_CRITERIA[b.criteria!] || "";
        return aKey.localeCompare(bKey);
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "20%",
      sorter: (a: TKPI, b: TKPI) => a.description.localeCompare(b.description),
      onFilter: (value: string | number | boolean, record: TKPI) =>
        record.description?.includes(value as string),
    },
    {
      title: "Action",
      key: "actions",
      align: "left",
      width: "6%",
      render: (item: TKPI) => (
        <Dropdown
          trigger={["click"]}
          getPopupContainer={() => document.body}
          menu={{
            items: [
              //  Utils.isUserHasAccess(
              //   context.user?.privilegeList,
              //   ePrivileges.EDITDesignation
              // ) &&
              {
                key: "edit",
                label: (
                  <CustomDrawer
                    size="default"
                    trigger={
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          alignItems: "center",
                        }}
                      >
                        <EditOutlined style={{ fontSize: 12 }} />
                        Edit
                      </div>
                    }
                    component={KPIForm}
                    componentProps={{
                      id: item.id,
                      onRefreshList: () => {
                        fetchData();
                      },
                    }}
                    title="Edit KPI"
                  />
                ),
              },
              {
                key: "delete",
                danger: true,
                label: (
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      alignItems: "center",
                    }}
                  >
                    <DeleteOutlined style={{ fontSize: 12 }} />
                    Delete
                  </div>
                ),
                onClick: () => handleDelete(item),
              },
            ].filter(Boolean) as any,
          }}
        >
          <MoreOutlined style={{ fontSize: 20, cursor: "pointer" }} />
        </Dropdown>
      ),
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);

  const context: any = useContext(UserContext);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1, // Fetch all data for client-side filtering
        },
      };

      const response = await post(GetKPIs, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map((item: TKPI, index: number) => ({
          ...item,
          key: (item.id ?? "").toString(),
          name: item.name,
          description: item.description,
          id: item.id,
          criteria: item.criteria,
          designations: item.designationMappings?.map((m) => m.designation).filter(Boolean) || [],
        }));
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);

        const nameFilters = Array.from(
          new Set(data.map((item: TKPI) => item.name))
        ).map((name) => ({ text: name, value: name }));

        const designationFilters = Array.from(
          new Set(
            formattedData.flatMap((item: any) =>
              item.designations?.map((d: any) => d.name) || []
            )
          )
        )
          .filter(Boolean)
          .map((name) => ({ text: name, value: name }));

        setColumns((prevColumns) =>
          prevColumns.map((col) => {
            if (col.key === "name") {
              return { ...col, filters: nameFilters };
            }
            if (col.key === "designations") {
              return { ...col, filters: designationFilters };
            }
            return col;
          })
        );
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) => {
    const search = searchText.toLowerCase();
    const matchesName = item.name?.toLowerCase().includes(search);
    const matchesDescription = item.description?.toLowerCase().includes(search);
    const matchesDesignation = item.designations?.some((d) =>
      d.name?.toLowerCase().includes(search)
    );

    // Check enum string value for Criteria
    const criteriaStr =
      item.criteria !== undefined
        ? utils
          .formatEnumLabel(eEVALUATION_CRITERIA[item.criteria])
          .toLowerCase()
        : "";
    const matchesCriteria = criteriaStr.includes(search);

    return (
      matchesName || matchesDescription || matchesDesignation || matchesCriteria
    );
  });

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const handleResetAll = () => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };
  // Return JSX structure for User Component
  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardOutlined />}
        title={`${filterParams?.totalRows} Total KPIs`}
        size="large"
      >
        <Space>
          {/* {Utils.isUserHasAccess(
            context.user?.privilegeList,
            ePrivileges.ADDPROJECTCATEGORY
          ) ? ( */}
          <CustomDrawer
            size="default"
            trigger={
              <Button disabled={isLoading} type="primary">
                Add
              </Button>
            }
            component={KPIForm}
            componentProps={{
              id: 0, // Pass necessary props for ServicePartnerForm
              onRefreshList: () => {
                fetchData();
                setFilterParams(defaultFilterParams);
              },
            }}
            title="Add New KPI"
          />
          {/* ) : null} */}

          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            prefix={
              <SearchOutlined style={{ color: "var( --bs-search-style)" }} />
            }
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
          {/* <FontAwesomeIcon
          icon={faFilter}
          className="listFilterIcons"
        ></FontAwesomeIcon> */}
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
}
